import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_BASE_URL } from "@/lib/constants/app";
import { supabase } from "@/lib/supabase";
import { formatDate, formatFileSize } from "@/lib/utils";
import { createVectorStore as createVectorStoreUtil } from "@/lib/utils/vectorStoreUtils";
import useAuthStore from "@/store/authStore";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  AlertCircle,
  Download,
  Files,
  FileText,
  Loader,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SortableFileItem } from "./components/SortableFileItem";
import { SortableFileTableRow } from "./components/SortableFileTableRow";

// Extract the vector store creation function for reuse
export const createVectorStore = async (name) => {
  try {
    const vectorStoreId = await createVectorStoreUtil(name);
    return vectorStoreId;
  } catch (createError) {
    console.error("Error creating vector store:", createError);
    throw new Error(`Failed to create vector store: ${createError.message}`);
  }
};

// Updated file attachment function to use proper two-step backend API workflow
export const attachFileToVectorStore = async (fileData, vectorStoreId) => {
  try {
    if (!vectorStoreId) {
      throw new Error("Vector store ID is required");
    }

    if (!fileData.file) {
      throw new Error("File data is incomplete");
    }

    // Convert file to base64 for the API
    const fileContent = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(fileData.file);
    });

    // Step 1: Upload file to OpenAI
    const uploadResponse = await fetch(`${API_BASE_URL}/api/ai/files/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        file: fileContent,
        filename: fileData.file.name,
        purpose: "assistants",
      }),
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(`Failed to upload file: ${errorData.error}`);
    }

    const uploadResult = await uploadResponse.json();
    const fileId = uploadResult.file.id;

    // Step 2: Attach file to vector store
    const attachResponse = await fetch(
      `${API_BASE_URL}/api/ai/vector-store/${vectorStoreId}/files/attach`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_id: fileId,
        }),
      }
    );

    if (!attachResponse.ok) {
      const errorData = await attachResponse.json();
      throw new Error(
        `Failed to attach file to vector store: ${errorData.error}`
      );
    }

    const attachResult = await attachResponse.json();

    return {
      fileId: fileId,
      vectorStoreFileId: attachResult.uploadResponse.id,
    };
  } catch (error) {
    console.error("Error in file upload and attach workflow:", error);
    throw error;
  }
};

export const WorkspaceFiles = ({
  workspaceId,
  onUpload,
  vectorStoreId,
  open,
  onOpenChange,
}) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const fileInputRef = useRef(null);

  const {
    user,
    updateWorkspaceFiles,
    currentWorkspace,
    setCurrentWorkspace,
    workspaces,
    setWorkspaces,
  } = useAuthStore();

  // Load files on mount
  useEffect(() => {
    if (open) {
      loadFiles();
    }
  }, [workspaceId, open]);

  const loadFiles = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // First get the workspace to retrieve the file IDs
      const { data: workspace, error: workspaceError } = await supabase
        .from("workspaces")
        .select("workspace_files")
        .eq("id", workspaceId)
        .single();

      if (workspaceError) throw workspaceError;

      // If the workspace has no files, return an empty array
      if (
        !workspace.workspace_files ||
        workspace.workspace_files.length === 0
      ) {
        setFiles([]);
        setIsLoading(false);
        return;
      }

      // Get the files using the workspace_files array
      // Using 'in' operator to filter by file IDs from the workspace_files array
      const { data: fileData, error } = await supabase
        .from("workspace_files")
        .select("*")
        .in("id", workspace.workspace_files)
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false }); // Fallback for files without display_order

      if (error) throw error;

      setFiles(fileData);
    } catch (err) {
      console.error("Error loading files:", err);
      setError(err.message);

      // Fallback to the old method if there's an error
      try {
        const { data: files, error } = await supabase
          .from("workspace_files")
          .select("*")
          .eq("workspace_id", workspaceId)
          .order("display_order", { ascending: true })
          .order("created_at", { ascending: false }); // Fallback for files without display_order

        if (error) throw error;
        setFiles(files);
      } catch (fallbackErr) {
        console.error("Error in fallback file loading:", fallbackErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const VECTOR_STORE_SUPPORTED_EXTENSIONS = [
    ".c",
    ".cpp",
    ".csv",
    ".docx",
    ".html",
    ".java",
    ".json",
    ".md",
    ".pdf",
    ".php",
    ".pptx",
    ".py",
    ".rb",
    ".tex",
    ".txt",
    ".css",
    ".jsx",
    ".tsx",
    ".js",
    ".ts",
  ];

  const isFileTypeSupported = (fileName) => {
    const extension = `.${fileName.split(".").pop().toLowerCase()}`;
    return VECTOR_STORE_SUPPORTED_EXTENSIONS.includes(extension);
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) setIsDragging(true);
    },
    [isDragging]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide drag overlay if leaving the dialog entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Check if any files are images or xlsx files and filter them out
      const validFiles = files.filter((file) => {
        if (file.type.startsWith("image/")) {
          console.warn(`Skipping image file: ${file.name}`);
          return false;
        }
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        if (
          fileExtension === "xlsx" ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          console.warn(`Skipping xlsx file: ${file.name}`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        setError(
          "Image files and Excel (.xlsx) files are not allowed. Please upload documents, text files, or other supported file types."
        );
        return;
      }

      if (validFiles.length < files.length) {
        setError(
          `Skipped ${files.length - validFiles.length} unsupported file(s) (images and .xlsx files). Only supported files were processed.`
        );
      }

      handleMultipleFileUpload(validFiles);
    }
  }, []);

  const handleAddFilesClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Check if any files are images or xlsx files and filter them out
    const validFiles = files.filter((file) => {
      if (file.type.startsWith("image/")) {
        console.warn(`Skipping image file: ${file.name}`);
        return false;
      }
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        fileExtension === "xlsx" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        console.warn(`Skipping xlsx file: ${file.name}`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      setError(
        "Image files and Excel (.xlsx) files are not allowed. Please upload documents, text files, or other supported file types."
      );
      event.target.value = null; // Clear the input
      return;
    }

    if (validFiles.length < files.length) {
      setError(
        `Skipped ${files.length - validFiles.length} unsupported file(s) (images and .xlsx files). Only supported files were processed.`
      );
    }

    await handleMultipleFileUpload(validFiles);
    event.target.value = null; // Clear the input
  };

  const handleMultipleFileUpload = async (files) => {
    if (!files.length) return;

    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      // Add files to uploading state immediately
      const uploadingFileEntries = files.map((file, index) => ({
        id: `uploading-${Date.now()}-${index}`,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        isUploading: true,
        uploadProgress: 0,
      }));
      setUploadingFiles(uploadingFileEntries);

      // FIRST: Check if user has permission to upload files to this workspace
      const { data: workspaceMembership, error: membershipError } =
        await supabase
          .from("workspace_members")
          .select("role, permissions")
          .eq("workspace_id", workspaceId)
          .eq("user_id", user.id)
          .single();

      if (membershipError) {
        console.error("Error checking workspace permissions:", membershipError);
        throw new Error(
          "Could not verify workspace permissions. Please try again."
        );
      }

      if (!workspaceMembership) {
        throw new Error("You are not a member of this workspace");
      }

      // Check if user has permission to upload files based on role or explicit permissions
      const canUpload =
        workspaceMembership.role === "owner" ||
        workspaceMembership.permissions?.files?.upload === true;

      if (!canUpload) {
        throw new Error(
          "You do not have permission to upload files to this workspace"
        );
      }

      // Validate file sizes (limit to 100MB each)
      const oversizedFiles = files.filter(
        (file) => file.size > 100 * 1024 * 1024
      );
      if (oversizedFiles.length > 0) {
        throw new Error(
          `The following files exceed the maximum size limit (100MB): ${oversizedFiles.map((f) => f.name).join(", ")}`
        );
      }

      // NEW: Check if workspace has a vector store, create one if it doesn't
      let currentVectorStoreId = vectorStoreId;

      if (!currentVectorStoreId) {
        try {
          // Create a new vector store using the utility function
          currentVectorStoreId = await createVectorStore(
            `${currentWorkspace?.name || workspaceId} Vector Store`
          );

          // Update the workspace in the database
          const { data: updatedWorkspace, error: updateError } = await supabase
            .from("workspaces")
            .update({
              vector_store_id: currentVectorStoreId,
              metadata: {
                ...(currentWorkspace?.metadata || {}),
                vector_store_id: currentVectorStoreId,
                vector_store_created_at: new Date().toISOString(),
                vector_store_provider: "openai",
              },
            })
            .eq("id", workspaceId)
            .select()
            .single();

          if (updateError) {
            console.error(
              "Error updating workspace with vector store ID:",
              updateError
            );
            throw new Error(
              `Failed to update workspace: ${updateError.message}`
            );
          }

          // Update the auth store state
          if (currentWorkspace && currentWorkspace.id === workspaceId) {
            const updatedCurrentWorkspace = {
              ...currentWorkspace,
              vector_store_id: currentVectorStoreId,
              metadata: {
                ...(currentWorkspace.metadata || {}),
                vector_store_id: currentVectorStoreId,
                vector_store_created_at: new Date().toISOString(),
                vector_store_provider: "openai",
              },
            };

            setCurrentWorkspace(updatedCurrentWorkspace);

            // Also update the workspaces array
            const updatedWorkspaces = workspaces.map((workspace) =>
              workspace.id === workspaceId ? updatedCurrentWorkspace : workspace
            );
            setWorkspaces(updatedWorkspaces);
          }
        } catch (vectorStoreError) {
          console.error("Error creating vector store:", vectorStoreError);
          throw new Error(
            `Failed to create vector store: ${vectorStoreError.message}`
          );
        }
      }

      const uploadedFiles = [];
      const totalFiles = files.length;
      let completedFiles = 0;

      // Process files sequentially to avoid overwhelming the system
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          // Update progress for file processing start
          const overallProgress = Math.round(
            (completedFiles / totalFiles) * 100
          );
          setUploadProgress(overallProgress);

          // Update individual file progress
          setUploadingFiles((prev) =>
            prev.map((f, index) =>
              index === i ? { ...f, uploadProgress: 25 } : f
            )
          );

          // STEP 1: Create minimal file record in database (NO file content storage)
          const fileRecord = {
            workspace_id: workspaceId,
            user_id: user.id,
            created_by: user.id,
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
            // Remove file_content completely - don't store in database
            file_path: `${workspaceId}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`, // Generate a virtual path for reference
            storage_path: `vector_store/${currentVectorStoreId}/${file.name}`, // Reference to vector store location
            metadata: {
              lastModified: file.lastModified,
              type: file.type,
              purpose: "workspace",
              original_filename: file.name,
              vector_store_attached: false,
              storage_type: "vector_store_only", // Indicate this file is only in vector store
              storage_confirmed: false, // Will be set to true after successful upload
            },
          };

          // Update file progress
          setUploadingFiles((prev) =>
            prev.map((f, index) =>
              index === i ? { ...f, uploadProgress: 50 } : f
            )
          );

          // Insert file record without content
          const { data: insertedFile, error: recordError } = await supabase
            .from("workspace_files")
            .insert(fileRecord)
            .select()
            .single();

          if (recordError) {
            console.error("Database insert error:", recordError);
            throw new Error(
              `Failed to create file record for ${file.name}: ${recordError.message}`
            );
          }

          // Update file progress
          setUploadingFiles((prev) =>
            prev.map((f, index) =>
              index === i ? { ...f, uploadProgress: 75 } : f
            )
          );

          // STEP 2: Upload to vector store directly
          let updatedFile = insertedFile;

          try {
            // Check if file type is supported for vector stores
            if (!isFileTypeSupported(file.name)) {
              // Update file record but mark as incompatible with vector store
              const { data: vectorStoreFile, error: updateError } =
                await supabase
                  .from("workspace_files")
                  .update({
                    metadata: {
                      ...insertedFile.metadata,
                      vector_store_incompatible: true,
                      vector_store_incompatible_reason:
                        "File type not supported for vector stores",
                      storage_confirmed: true, // Mark as processed even if not uploaded
                    },
                  })
                  .eq("id", insertedFile.id)
                  .select()
                  .single();

              if (!updateError) {
                updatedFile = vectorStoreFile;
              }
            } else {
              // Upload file to OpenAI vector store directly

              try {
                // Use the utility function to attach file to vector store
                const attachResult = await attachFileToVectorStore(
                  { file: file },
                  currentVectorStoreId
                );

                // Update file record with vector store info and mark as confirmed
                const { data: vectorStoreFile, error: updateError } =
                  await supabase
                    .from("workspace_files")
                    .update({
                      vector_store_id: currentVectorStoreId,
                      metadata: {
                        ...insertedFile.metadata,
                        vector_store_attached: true,
                        vector_store_id: currentVectorStoreId,
                        vector_store_attached_at: new Date().toISOString(),
                        vector_store_file_id: attachResult.vectorStoreFileId,
                        openai_file_id: attachResult.fileId,
                        provider: "openai",
                        storage_confirmed: true, // Mark as successfully stored
                        storage_type: "vector_store_only",
                      },
                    })
                    .eq("id", insertedFile.id)
                    .select()
                    .single();

                if (updateError) throw updateError;

                updatedFile = vectorStoreFile;
              } catch (uploadError) {
                console.error(
                  `Error in file upload process for ${file.name}:`,
                  uploadError
                );

                // Update file record to mark attachment failure but keep the record
                await supabase
                  .from("workspace_files")
                  .update({
                    metadata: {
                      ...insertedFile.metadata,
                      vector_store_attachment_failed: true,
                      vector_store_error:
                        uploadError.message ||
                        "Failed to upload to vector store",
                      storage_confirmed: false,
                    },
                  })
                  .eq("id", insertedFile.id);

                throw uploadError; // Re-throw to handle in outer catch
              }
            }
          } catch (vectorStoreError) {
            console.error(
              `Error with vector store operations for ${file.name}:`,
              vectorStoreError
            );

            // Update file record to mark attachment failure
            await supabase
              .from("workspace_files")
              .update({
                metadata: {
                  ...insertedFile.metadata,
                  vector_store_attachment_failed: true,
                  vector_store_error:
                    vectorStoreError.message ||
                    "Failed to handle vector store operations",
                  storage_confirmed: false,
                },
              })
              .eq("id", insertedFile.id);

            // Continue without failing the whole upload for this file
            console.warn(
              `Continuing upload process despite error with ${file.name}`
            );
          }

          // Complete file progress
          setUploadingFiles((prev) =>
            prev.map((f, index) =>
              index === i ? { ...f, uploadProgress: 100 } : f
            )
          );

          uploadedFiles.push(updatedFile);
          completedFiles++;

          // Update progress
          setUploadProgress(Math.round((completedFiles / totalFiles) * 100));
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          // Mark file as failed
          setUploadingFiles((prev) =>
            prev.map((f, index) =>
              index === i
                ? { ...f, uploadProgress: 0, error: fileError.message }
                : f
            )
          );
          // Continue with other files, but log the error
          completedFiles++;
          setUploadProgress(Math.round((completedFiles / totalFiles) * 100));
        }
      }

      if (uploadedFiles.length === 0) {
        throw new Error("No files were successfully uploaded");
      }

      // STEP 3: Add all the file IDs to the workspace's workspace_files array
      if (uploadedFiles.length > 0) {
        // First, get the current workspace_files array
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("workspace_files")
          .eq("id", workspaceId)
          .single();

        if (!workspaceError) {
          // Add the new file IDs to the array, ensuring no duplicates
          const currentFiles = workspace.workspace_files || [];
          const newFileIds = uploadedFiles.map((f) => f.id);
          const updatedFiles = [...new Set([...currentFiles, ...newFileIds])];

          // Update the workspace with the new array
          const { error: updateError } = await supabase
            .from("workspaces")
            .update({ workspace_files: updatedFiles })
            .eq("id", workspaceId);

          if (!updateError) {
            // Update the auth store with the new file IDs
            if (typeof updateWorkspaceFiles === "function") {
              newFileIds.forEach((fileId) => {
                updateWorkspaceFiles(workspaceId, fileId, "add");
              });
            } else {
              console.warn(
                "updateWorkspaceFiles function not available in authStore"
              );
            }
          }
        }
      }

      // Update workspace stats for all uploaded files
      try {
        // Get current workspace metadata
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("metadata")
          .eq("id", workspaceId)
          .single();

        if (!workspaceError) {
          const totalSize = uploadedFiles.reduce(
            (sum, file) => sum + file.file_size,
            0
          );

          // Initialize metadata and stats if they don't exist
          const updatedMetadata = {
            ...(workspace.metadata || {}),
            stats: {
              ...(workspace.metadata?.stats || {}),
              file_count:
                (workspace.metadata?.stats?.file_count || 0) +
                uploadedFiles.length,
              total_storage:
                (workspace.metadata?.stats?.total_storage || 0) + totalSize,
            },
          };

          // Update workspace metadata directly
          await supabase
            .from("workspaces")
            .update({ metadata: updatedMetadata })
            .eq("id", workspaceId);
        }
      } catch (statsError) {
        console.error("Error updating workspace stats:", statsError);
        // Don't fail the upload if stats update fails
      }

      // Update files list with all uploaded files
      setFiles((prev) => [...uploadedFiles, ...prev]);

      // If custom upload handler provided, call it for each file
      if (onUpload && typeof onUpload === "function") {
        for (const file of uploadedFiles) {
          await onUpload(file);
        }
      }

      // Show success message
      if (uploadedFiles.length === files.length) {
        console.log(`Successfully uploaded ${uploadedFiles.length} file(s)`);
      } else {
        setError(
          `Successfully uploaded ${uploadedFiles.length} out of ${files.length} file(s). Some files may have failed.`
        );
      }

      return uploadedFiles;
    } catch (err) {
      console.error("Error uploading files:", err);

      if (
        err.message.includes("row-level security policy") ||
        err.statusCode === "403"
      ) {
        setError(
          "Permission denied: You do not have permission to upload files to this workspace."
        );
      } else {
        setError(err.message || "An error occurred during file upload");
      }
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFiles([]);
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      setIsDeleting(true);
      setError(null);

      // Get file before deletion to get size and vector store info
      const { data: file, error: getError } = await supabase
        .from("workspace_files")
        .select("*")
        .eq("id", fileId)
        .single();

      if (getError) throw getError;

      // Delete from vector store if attached
      if (file.vector_store_id && file.metadata?.vector_store_file_id) {
        try {
          // Import and use the ChatCompletionService
          const { chatCompletionService } = await import(
            "@/services/openai/chatCompletionService"
          );
          await chatCompletionService.deleteFileFromVectorStore(
            file.vector_store_id,
            file.metadata.vector_store_file_id
          );
        } catch (vectorStoreError) {
          console.error(
            "Error deleting file from vector store:",
            vectorStoreError
          );
          // Continue with file deletion despite vector store error
        }
      }

      // Delete from database (no need to delete from storage anymore)
      const { error: deleteError } = await supabase
        .from("workspace_files")
        .delete()
        .eq("id", fileId);

      if (deleteError) throw deleteError;

      // Remove the file ID from the workspace_files array
      // First, get the current workspace_files array
      const { data: workspace, error: workspaceError } = await supabase
        .from("workspaces")
        .select("workspace_files")
        .eq("id", workspaceId)
        .single();

      if (!workspaceError) {
        const currentFiles = workspace.workspace_files || [];
        const updatedFiles = currentFiles.filter((id) => id !== fileId);

        // Update the workspace with the new array
        await supabase
          .from("workspaces")
          .update({ workspace_files: updatedFiles })
          .eq("id", workspaceId);

        // Update the auth store by removing the file ID
        if (typeof updateWorkspaceFiles === "function") {
          updateWorkspaceFiles(workspaceId, fileId, "remove");
        } else {
          console.warn(
            "updateWorkspaceFiles function not available in authStore"
          );
        }
      }

      // Update workspace stats directly
      try {
        // Get current workspace metadata
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("metadata")
          .eq("id", workspaceId)
          .single();

        if (!workspaceError) {
          // Initialize metadata and stats if they don't exist
          const currentFileCount = workspace.metadata?.stats?.file_count || 1;
          const currentStorage =
            workspace.metadata?.stats?.total_storage || file.file_size;

          const updatedMetadata = {
            ...(workspace.metadata || {}),
            stats: {
              ...(workspace.metadata?.stats || {}),
              file_count: Math.max(0, currentFileCount - 1),
              total_storage: Math.max(0, currentStorage - file.file_size),
            },
          };

          // Update workspace metadata directly
          await supabase
            .from("workspaces")
            .update({ metadata: updatedMetadata })
            .eq("id", workspaceId);
        }
      } catch (statsError) {
        console.error(
          "Error updating workspace stats during deletion:",
          statsError
        );
        // Don't fail the deletion if stats update fails
      }

      // Update files list
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      console.error("Error deleting file:", err);
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      // Since we no longer store file content in database, we need to download from OpenAI
      if (!file.metadata?.openai_file_id) {
        throw new Error(
          "File is not available for download - no OpenAI file ID found"
        );
      }

      // Use the chat completion service to retrieve file content from OpenAI
      const { chatCompletionService } = await import(
        "@/services/openai/chatCompletionService"
      );

      const fileBlob = await chatCompletionService.retrieveFileContent(
        file.metadata.openai_file_id
      );

      // Create download link
      const url = window.URL.createObjectURL(fileBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading file:", err);
      setError(`Download failed: ${err.message}`);
    }
  };

  // Render as dialog
  if (open) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] w-[800px] h-[600px] bg-white dark:bg-zinc-900 border-[#3a3a3a] text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
          hideCloseButton={true}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <DialogTitle className="sr-only">Project Files</DialogTitle>
          <DialogDescription className="sr-only">
            Manage and upload files for your project workspace
          </DialogDescription>

          {/* Drag and Drop Overlay */}
          {isDragging && (
            <div className="absolute inset-0 bg-blue-600/20 border-2 border-dashed border-blue-400 rounded-2xl flex items-center justify-center z-50">
              <div className="text-center">
                <Files className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                <p className="text-blue-300 text-lg font-medium">
                  Drop files here to upload
                </p>
                <p className="text-blue-300/70 text-sm mt-2">
                  Supported formats: Documents, code files, text files
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Project files
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAddFilesClick}
                className="bg-transparent hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-white px-4 py-2 rounded-full text-sm font-medium border border-gray-900 dark:border-white hover:border-gray-900 dark:hover:border-white"
                disabled={isUploading}
              >
                Add files
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".c,.cpp,.csv,.docx,.html,.java,.json,.md,.pdf,.php,.pptx,.py,.rb,.tex,.txt,.css,.jsx,.tsx,.js,.ts"
            onChange={handleFileUpload}
            disabled={isUploading}
            multiple
            className="hidden"
          />

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
            <div className="flex-1 overflow-auto">
              <div className="p-4">
                {/* Error Message */}
                {error && (
                  <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800/30 mb-4">
                    {error}
                  </div>
                )}

                {/* Loading State */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500 dark:text-gray-400 flex items-center gap-3">
                      <Loader className="h-5 w-5 animate-spin text-blue-500" />
                      Loading files...
                    </div>
                  </div>
                ) : (
                  /* Files List */
                  <div className="space-y-3">
                    {/* Show uploading files first */}
                    {uploadingFiles.map((file) => (
                      <div
                        key={file.id}
                        className="group flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                          {file.error ? (
                            <AlertCircle className="h-5 w-5 text-white" />
                          ) : (
                            <Loader className="h-5 w-5 text-white animate-spin" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col">
                            <span className="text-gray-900 dark:text-white font-medium truncate">
                              {file.file_name}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 text-sm">
                              File
                            </span>
                            {file.error ? (
                              <span className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Upload failed: {file.error}
                              </span>
                            ) : (
                              <span className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                Uploading... {file.uploadProgress}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleFileDelete(file.id)}
                            disabled={isDeleting}
                            className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:z-10 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {/* Show existing files */}
                    <SortableContext
                      items={files.map((f) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {files.map((file) => (
                        <SortableFileItem
                          key={file.id}
                          file={file}
                          onDelete={handleFileDelete}
                          isDeleting={isDeleting}
                        >
                          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col">
                              <span className="text-gray-900 dark:text-white font-medium truncate">
                                {file.file_name}
                              </span>
                              <span className="text-gray-500 dark:text-gray-400 text-sm">
                                File
                              </span>
                              {file.metadata?.vector_store_incompatible && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1">
                                  <AlertCircle className="h-3 w-3" />
                                  Not supported for vector search
                                </span>
                              )}
                              {file.metadata
                                ?.vector_store_attachment_failed && (
                                <span
                                  className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1"
                                  title={
                                    file.metadata?.vector_store_error ||
                                    "Failed to attach"
                                  }
                                >
                                  <AlertCircle className="h-3 w-3" />
                                  Upload failed
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleFileDelete(file.id)}
                              disabled={isDeleting}
                              className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:z-10 transition-opacity"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </SortableFileItem>
                      ))}
                    </SortableContext>

                    {/* Empty state */}
                    {files.length === 0 && uploadingFiles.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="h-16 w-16 rounded-lg bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                          <Files className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 text-lg font-medium">
                          No files uploaded yet
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md text-center mt-2">
                          Upload files to make them available to your AI
                          assistant
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If not open as dialog, render the standard component
  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div
        className={`bg-white dark:bg-zinc-900 backdrop-blur-sm rounded-2xl border-2 ${
          isDragging
            ? "border-dashed border-blue-400/70 dark:border-blue-500/70 bg-blue-900/90 dark:bg-blue-900/90"
            : "border-gray-100 dark:border-zinc-800"
        } p-5 shadow-sm transition-all duration-300`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging ? (
          <div
            className={`
            flex
            items-center
            justify-center
            text-center
            transition-all
            duration-300
            min-h-[180px]
          `}
          >
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center mb-6">
                <Files className="h-6 w-6 text-blue-400 mr-2" />
                <p className="text-blue-300 text-lg font-medium">
                  Drop files here to add
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="bg-white dark:bg-white/90 w-10 h-12 rounded-2xl shadow-sm flex items-center justify-center">
                  <span className="text-gray-500 text-xs font-mono">TXT</span>
                </div>
                <div className="bg-blue-500 text-white text-xs py-1 px-2 rounded-full whitespace-nowrap">
                  <span className="text-white/90 text-xs">file.txt</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-gray-800 dark:text-zinc-200 font-medium mb-1">
                Upload Files
              </h3>
              <p className="text-sm text-gray-600 dark:text-zinc-400 mb-3">
                Upload files to your project (images not supported)
              </p>

              {/* Regular Upload UI */}
              <div className="rounded-2xl p-4">
                <Input
                  type="file"
                  accept=".c,.cpp,.csv,.docx,.html,.java,.json,.md,.pdf,.php,.pptx,.py,.rb,.tex,.txt,.css,.jsx,.tsx,.js,.ts"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  multiple
                  className={[
                    "max-w-full w-full cursor-pointer",
                    "bg-gray-50 dark:bg-zinc-800/50",
                    "border-2 border-indigo-100 dark:border-indigo-900/30",
                    "hover:bg-white/[0.9] dark:hover:bg-zinc-800",
                    "hover:border-indigo-300 dark:hover:border-indigo-700",
                    "transition-all duration-200",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-indigo-500/30",
                    "focus:border-indigo-500/50",
                    "backdrop-blur-[0.5px]",
                    "text-gray-600 dark:text-indigo-300",
                    "h-12",
                    "px-0 py-0",
                    "file:border-0",
                    "file:bg-indigo-50 dark:file:bg-indigo-900/30",
                    "file:text-indigo-600 dark:file:text-indigo-300",
                    "file:px-4",
                    "file:h-12",
                    "file:mr-3",
                    "file:rounded-l-2xl",
                    "file:font-medium",
                    "file:cursor-pointer",
                    "file:border-r",
                    "file:border-r-indigo-100",
                    "file:dark:border-r-indigo-800/30",
                    "disabled:opacity-50",
                    "disabled:cursor-not-allowed",
                    "rounded-2xl",
                    "flex items-center",
                  ].join(" ")}
                />
                <p className="text-sm text-center text-gray-500 dark:text-zinc-500 mt-3">
                  or drag and drop multiple files here
                </p>
              </div>
            </div>
            {isUploading && (
              <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 py-2 px-3 rounded-2xl text-indigo-700 dark:text-indigo-300 text-sm border border-indigo-100 dark:border-indigo-800/30">
                <Loader className="h-4 w-4 animate-spin" />
                Uploading... {uploadProgress}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-2xl border border-red-100 dark:border-red-800/30">
          Error: {error}
        </div>
      )}

      {/* Files Table */}
      <div className="border border-gray-100 dark:border-zinc-800 rounded-2xl overflow-hidden bg-white dark:bg-zinc-900 backdrop-blur-sm shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-zinc-800/50">
              <TableHead className="font-medium text-gray-700 dark:text-zinc-300">
                Name
              </TableHead>
              <TableHead className="font-medium text-gray-700 dark:text-zinc-300">
                Type
              </TableHead>
              <TableHead className="font-medium text-gray-700 dark:text-zinc-300">
                Size
              </TableHead>
              <TableHead className="font-medium text-gray-700 dark:text-zinc-300">
                Uploaded
              </TableHead>
              <TableHead className="font-medium text-gray-700 dark:text-zinc-300 text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableContext
              items={files.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {files.map((file) => (
                <SortableFileTableRow key={file.id} file={file}>
                  <TableCell className="font-medium text-gray-800 dark:text-zinc-200 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="truncate max-w-[200px]">
                          {file.file_name}
                        </span>
                        {file.metadata?.vector_store_incompatible && (
                          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Not supported for vector search
                          </span>
                        )}
                        {file.metadata?.vector_store_attachment_failed && (
                          <span
                            className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
                            title={
                              file.metadata?.vector_store_error ||
                              "Failed to attach"
                            }
                          >
                            <AlertCircle className="h-3 w-3" />
                            Upload failed
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-zinc-400 py-3">
                    <span className="px-2 py-1 bg-gray-100 dark:bg-zinc-800 rounded-md text-xs">
                      {file.file_type.split("/")[1]?.toUpperCase() ||
                        file.file_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-zinc-400 py-3">
                    {formatFileSize(file.file_size)}
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-zinc-400 py-3">
                    {formatDate(file.created_at)}
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleFileDownload(file)}
                        disabled={
                          !file.metadata?.storage_confirmed ||
                          file.metadata?.vector_store_attachment_failed ||
                          !file.metadata?.openai_file_id
                        }
                        className="h-8 w-8 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800/30 border border-indigo-100 dark:border-indigo-800/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleFileDelete(file.id)}
                        className="h-8 w-8 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/30 border border-red-100 dark:border-red-800/30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </SortableFileTableRow>
              ))}
            </SortableContext>
            {files.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="h-16 w-16 rounded-2xl bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-2">
                      <Files className="h-8 w-8 text-gray-400 dark:text-zinc-600" />
                    </div>
                    <p className="text-gray-600 dark:text-zinc-400">
                      No files uploaded yet
                    </p>
                    <p className="text-sm text-gray-500 dark:text-zinc-500 max-w-md text-center">
                      Upload files directly to vector store for AI assistant
                      access
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WorkspaceFiles;
